// frontend/admin-portal/src/pages/Products.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Checkbox,
  Grid,
  ViewToggle,
  formatMoney,
  MarginBadge,
  computeMargin,
} from "@fiberhome/shared-ui";
import { usePersistedState } from "@fiberhome/shared-ui";
import { productService } from "../services/productService";
import { categoryService } from "../services/categoryService";
import { brandService } from "../services/brandService";
import { useSettings } from "../contexts/SettingsContext";
import ProductStats from "../components/products/ProductStats";
import ProductFilters from "../components/products/ProductFilters";
import ProductModal from "../components/products/ProductModal";
import ProductCategoryBadge from "../components/products/ProductCategoryBadge";
import ExportProductsModal from "../components/products/ExportProductsModal";
import BulkDeleteModal from "../components/products/BulkDeleteModal";
import { resolveImageUrl } from "../services/fileService";
import BulkPriceUpdateModal from "../components/products/BulkPriceUpdateModal";
import ProductLabelsModal from "../components/products/ProductLabelsModal";
import CategoryStatsCard from "../components/products/CategoryStatsCard";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

export default function Products() {
  const navigate = useNavigate();
  const { get } = useSettings();
  const currency = get("finance.currencySymbol", "IQD");
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);

  const [view, setView] = usePersistedState("fiberhome.products.view", "list");
  const [perPage, setPerPage] = usePersistedState(
    "fiberhome.products.perPage",
    20,
  );

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: perPage,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: perPage,
    search: "",
    categoryId: "",
    brandId: "",
    kind: "",
    lowStock: "",
  });

  const [labelsOpen, setLabelsOpen] = useState(false);

  useEffect(() => {
    setFilters((f) => ({ ...f, limit: perPage, page: 1 }));
  }, [perPage]);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  useEffect(() => {
    setSelectedIds([]);
  }, [
    filters.page,
    filters.search,
    filters.categoryId,
    filters.brandId,
    filters.kind,
    filters.lowStock,
  ]);

  const loadRefs = useCallback(async () => {
    const [cats, brs] = await Promise.all([
      categoryService.list({ isActive: true }),
      brandService.list({ isActive: true }),
    ]);
    setCategories(cats);
    setBrands(brs);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      }
      const { items, pagination } = await productService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRefs();
  }, [loadRefs]);
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleNew = () => {
    setEditing(null);
    setDuplicateSource(null);
    setModalOpen(true);
  };
  const handleEdit = (p) => {
    setDuplicateSource(null);
    setEditing(p);
    setModalOpen(true);
  };

  const handleDuplicate = (p) => {
    const clone = {
      ...p,
      sku: "", // schema regenerates; retry logic below handles collision
      barcode: "", // → null on submit
      slug: "", // → null on submit
      stock: 0,
      name: `${p.name} (copy)`,
      id: undefined,
    };
    setEditing(null);
    setDuplicateSource(clone);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDuplicateSource(null);
  };
  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
    load();
    setEditing(null);
    setDuplicateSource(null);
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await productService.remove(p.id);
      handleSaved();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };
  const handleBulkDeleted = () => {
    setSelectedIds([]);
    setRefreshKey((k) => k + 1);
    load();
  };

  const formatPrice = (n) => formatMoney(n, { symbol: currency });
  const selectedProducts = useMemo(
    () => items.filter((p) => selectedIds.includes(p.id)),
    [items, selectedIds],
  );

  const columns = [
    {
      key: "name",
      label: "Product",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.sku} {r.brand ? `· ${r.brand.name}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (r) => <ProductCategoryBadge category={r.category} />,
    },
    {
      key: "stock",
      label: "Stock",
      render: (r) => (
        <div className="d-flex align-items-center gap-2">
          <span>
            {r.stock} {r.unit}
          </span>
          {r.stock <= 0 && <Badge variant="danger">Out</Badge>}
          {r.stock > 0 && r.stock <= r.minStock && (
            <Badge variant="warning">Low</Badge>
          )}
        </div>
      ),
    },
    {
      key: "price",
      label: "Price",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{formatPrice(r.price)}</div>
          <div className="text-muted" style={{ fontSize: 11.5 }}>
            cost {formatPrice(r.totalCost)}
          </div>
        </div>
      ),
    },
    {
      key: "margin",
      label: "Margin",
      width: 100,
      render: (r) => (
        <MarginBadge value={computeMargin(r.price, r.totalCost)} />
      ),
    },
    {
      key: "isActive",
      label: "Status",
      render: (r) => (
        <Badge variant={r.isActive ? "success" : "default"}>
          {r.isActive ? "Active" : "Hidden"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 260,
      render: (r) => (
        <div className="d-flex gap-1">
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/products/${r.id}`)}
          >
            View
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleEdit(r)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDuplicate(r)}
          >
            Duplicate
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const selectedCount = selectedIds.length;

  return (
    <>
      <PageHeader
        title="Products"
        subtitle="Catalog of fiber optic equipment"
        breadcrumbs={["Home", "Products"]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setExportOpen(true)}>
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/products/import")}
            >
              Import CSV
            </Button>
            <Button variant="primary" onClick={handleNew}>
              + New Product
            </Button>
          </div>
        }
      />

      <ProductStats refreshKey={refreshKey} />
      <CategoryStatsCard refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <ProductFilters
          filters={filters}
          onChange={setFilters}
          categories={categories}
          brands={brands}
        />
      </Card>

      {selectedCount > 0 && (
        <div className="fh-bulk-bar">
          <div className="fh-bulk-bar__info">
            <Checkbox
              checked={selectedCount === items.length && items.length > 0}
              indeterminate={selectedCount > 0 && selectedCount < items.length}
              onChange={(checked) =>
                setSelectedIds(checked ? items.map((p) => p.id) : [])
              }
              ariaLabel="Select all on this page"
            />
            <span>
              <strong>{selectedCount}</strong> selected
            </span>
          </div>
          <div className="fh-bulk-bar__actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds([])}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLabelsOpen(true)}
            >
              Print labels
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkPriceOpen(true)}
            >
              Update prices
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setBulkDeleteOpen(true)}
            >
              Delete selected
            </Button>
          </div>
        </div>
      )}

      <Card
        title={`Products (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar">
            <ViewToggle value={view} onChange={setView} size="sm" />
            <div className="fh-products-toolbar__per-page">
              <span className="text-muted" style={{ fontSize: 12 }}>
                Per page
              </span>
              <select
                className="fh-input fh-input--compact"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="fh-products-toolbar__pager">
              <Button
                size="sm"
                variant="ghost"
                disabled={pagination.page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              >
                Prev
              </Button>
              <span className="text-muted" style={{ fontSize: 12 }}>
                Page {pagination.page} / {pagination.pages || 1}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        {view === "list" ? (
          <Table
            columns={columns}
            data={items}
            loading={loading}
            emptyMessage="No products match your filters"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        ) : (
          <Grid
            imageColumn={(r) => {
              let imgs = r.images;

              // Handle string (JSON encoded array from MySQL)
              if (typeof imgs === "string") {
                try {
                  imgs = JSON.parse(imgs);
                } catch {
                  imgs = [];
                }
              }

              // If it's not an array by now, treat it as empty
              if (!Array.isArray(imgs)) imgs = [];

              // Get the first image
              const first = imgs[0];
              if (!first) return null;

              // If it's an object (like a FileRecord), use its URL
              if (typeof first === "object") {
                return resolveImageUrl(first.url || first.thumbnailUrl);
              }

              // Otherwise it's a string
              return resolveImageUrl(first);
            }}
            data={items}
            loading={loading}
            emptyMessage="No products match your filters"
            titleColumn="name"
            getId={(r) => r.id}
            onCardClick={(r) => navigate(`/products/${r.id}`)}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              {
                key: "category",
                label: "Category",
                span: 6,
                render: (r) => <ProductCategoryBadge category={r.category} />,
              },
              {
                key: "sku",
                label: "SKU",
                span: 6,
                render: (r) => (
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {r.sku}
                  </span>
                ),
              },
              {
                key: "stock",
                label: "Stock",
                span: 6,
                render: (r) => (
                  <div className="d-flex align-items-center gap-1">
                    <span>
                      {r.stock} {r.unit}
                    </span>
                    {r.stock <= 0 && <Badge variant="danger">Out</Badge>}
                    {r.stock > 0 && r.stock <= r.minStock && (
                      <Badge variant="warning">Low</Badge>
                    )}
                  </div>
                ),
              },
              {
                key: "price",
                label: "Price",
                span: 6,
                render: (r) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {formatPrice(r.price)}
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      cost {formatPrice(r.totalCost)}
                    </div>
                  </div>
                ),
              },
              {
                key: "margin",
                label: "Margin",
                span: 6,
                render: (r) => (
                  <MarginBadge value={computeMargin(r.price, r.totalCost)} />
                ),
              },
              {
                key: "isActive",
                label: "Status",
                span: 6,
                render: (r) => (
                  <Badge variant={r.isActive ? "success" : "default"}>
                    {r.isActive ? "Active" : "Hidden"}
                  </Badge>
                ),
              },
            ]}
            headerExtra={(r) => (
              <Badge variant={r.isActive ? "success" : "default"}>
                {r.isActive ? "Active" : "Hidden"}
              </Badge>
            )}
            footerExtra={(r) => (
              <>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => navigate(`/products/${r.id}`)}
                >
                  View
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(r)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDuplicate(r)}
                >
                  Duplicate
                </Button>
              </>
            )}
          />
        )}
      </Card>

      <ProductModal
        key={
          (editing?.id ? `edit-${editing.id}` : "") ||
          (duplicateSource ? `dup-${duplicateSource.name}` : "") ||
          "new"
        }
        open={modalOpen}
        product={editing || duplicateSource}
        isDuplicate={!!duplicateSource}
        categories={categories}
        brands={brands}
        lowStockThreshold={get("inventory.lowStockThreshold", 5)}
        onClose={handleCloseModal}
        onSaved={handleSaved}
      />

      <ExportProductsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        filters={{
          search: filters.search,
          categoryId: filters.categoryId,
          brandId: filters.brandId,
          kind: filters.kind,
          isActive: filters.isActive,
          lowStock: filters.lowStock,
        }}
        filteredCount={pagination.total}
      />

      <BulkDeleteModal
        open={bulkDeleteOpen}
        products={selectedProducts}
        onClose={() => setBulkDeleteOpen(false)}
        onDeleted={handleBulkDeleted}
      />

      <BulkPriceUpdateModal
        open={bulkPriceOpen}
        products={selectedProducts}
        onClose={() => setBulkPriceOpen(false)}
        onUpdated={handleBulkDeleted} // reuses the same "refresh + clear selection" handler
      />
      <ProductLabelsModal
        open={labelsOpen}
        products={selectedProducts}
        currency={currency}
        onClose={() => setLabelsOpen(false)}
      />
    </>
  );
}
